import type { RenewalDetails } from '@policyvision/contracts';
import {
  ArrowRight,
  ChevronDownIcon,
  ChevronUpIcon,
  ExternalLinkIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import MountainSvg from '@/assets/Mountain.svg';
import { Button } from '@/common/components/ui/button';

import DetailsSection from './analysis_tab/DetailsSection';
import PremiumChart from './analysis_tab/PremiumChart';
import TransactionsList from './analysis_tab/TransactionsList';

interface IRenewalAnalysisTabProps {
  renewalDetails: RenewalDetails;
  handleDrawerSwitch: () => void;
}

const RenewalAnalysisTab = ({
  renewalDetails,
  handleDrawerSwitch,
}: IRenewalAnalysisTabProps) => {
  const { t } = useTranslation();

  const [expandedSections, setExpandedSections] = useState<string[]>([
    'details',
    'primes',
    'amendments',
    'claims',
    'recommendations',
  ]);

  const toggleSection = (sectionName: string) => {
    if (expandedSections.includes(sectionName)) {
      const newExpandedSections = expandedSections.filter(
        (section) => section !== sectionName,
      );
      setExpandedSections(newExpandedSections);
    } else {
      const newExpandedSections = [...expandedSections, sectionName];
      setExpandedSections(newExpandedSections);
    }
  };

  return (
    <div className="px-4 pb-2">
      {/* Logique à changer */}
      {!['STANDBY', 'STANDBY_INSURER', 'COMPLETED'].includes(
        renewalDetails.status,
      ) && (
        <div className="relative flex items-center p-4 rounded-xl bg-terre-600 my-4 overflow-hidden">
          <img
            src={MountainSvg}
            alt=""
            className="absolute top-1/2 -translate-y-1/2 left--1/3 w-78.75 h-78.75 pointer-events-none"
          />
          <p className="text-white text-sm font-medium z-10">
            {t('renewal.drawer.newPolicyAlert')}
          </p>
          <Button
            variant="secondary"
            className="flex items-center gap-2.5 px-4 py-2 z-10"
            onClick={handleDrawerSwitch}
          >
            {t('renewal.drawer.viewComparator')}
            <ArrowRight />
          </Button>
        </div>
      )}
      <div className="rounded-md border border-border">
        <button
          className={`w-full flex items-center gap-2 bg-warm-gray-50 px-3 py-1 text-muted-foreground text-sm font-semilight border-b border-border cursor-pointer ${expandedSections.includes('details') ? 'rounded-t-md' : 'rounded-md'}`}
          onClick={() => toggleSection('details')}
          aria-expanded={expandedSections.includes('details')}
        >
          {expandedSections.includes('details') ? (
            <ChevronUpIcon className="w-3" />
          ) : (
            <ChevronDownIcon className="w-3" />
          )}
          {t('renewal.analysisTab.details')}
        </button>
        {expandedSections.includes('details') && (
          <DetailsSection renewalDetails={renewalDetails} />
        )}
      </div>
      <div className="rounded-md border border-border mt-4">
        <button
          className={`w-full flex items-center gap-2 bg-warm-gray-50 px-3 py-1 text-muted-foreground text-sm font-semilight border-b border-border cursor-pointer ${expandedSections.includes('primes') ? 'rounded-t-md' : 'rounded-md'}`}
          onClick={() => toggleSection('primes')}
          aria-expanded={expandedSections.includes('primes')}
        >
          {expandedSections.includes('primes') ? (
            <ChevronUpIcon className="w-3" />
          ) : (
            <ChevronDownIcon className="w-3" />
          )}
          {t('renewal.analysisTab.premiumEvolution')}
        </button>
        {expandedSections.includes('primes') && (
          <PremiumChart renewalDetails={renewalDetails} />
        )}
      </div>
      <div className="rounded-md border border-border mt-4">
        <button
          className={`w-full flex items-center gap-2 bg-warm-gray-50 px-3 py-1 text-muted-foreground text-sm font-semilight border-b border-border cursor-pointer ${expandedSections.includes('amendments') ? 'rounded-t-md' : 'rounded-md'}`}
          onClick={() => toggleSection('amendments')}
          aria-expanded={expandedSections.includes('amendments')}
        >
          {expandedSections.includes('amendments') ? (
            <ChevronUpIcon className="w-3" />
          ) : (
            <ChevronDownIcon className="w-3" />
          )}
          {t('renewal.analysisTab.amendments')}
        </button>
        {expandedSections.includes('amendments') && (
          <TransactionsList transactions={renewalDetails.amendments} />
        )}
      </div>
      <div className="rounded-md border border-border mt-4">
        <span
          className={`flex items-center justify-between bg-warm-gray-50 px-3 py-2 text-muted-foreground text-sm font-semilight border-border cursor-pointer ${expandedSections.includes('claims') ? 'rounded-t-md' : 'rounded-md'}`}
        >
          {t('renewal.analysisTab.claims')}
          <a href="#" target="_blank">
            <ExternalLinkIcon className="stroke-ring w-4" />
          </a>
        </span>
        {expandedSections.includes('amendments') && (
          <TransactionsList transactions={renewalDetails.claims} />
        )}
      </div>
      <div className="rounded-md border border-border mt-4">
        <button
          className={`w-full flex items-center gap-2 bg-warm-gray-50 px-3 py-1 text-muted-foreground text-sm font-semilight border-b border-border cursor-pointer ${expandedSections.includes('recommendations') ? 'rounded-t-md' : 'rounded-md'}`}
          onClick={() => toggleSection('recommendations')}
          aria-expanded={expandedSections.includes('recommendations')}
        >
          {expandedSections.includes('recommendations') ? (
            <ChevronUpIcon className="w-3" />
          ) : (
            <ChevronDownIcon className="w-3" />
          )}
          {t('renewal.analysisTab.recommendations')}
        </button>
        {expandedSections.includes('recommendations') && (
          <TransactionsList transactions={renewalDetails.recommendations} />
        )}
      </div>
    </div>
  );
};
export default RenewalAnalysisTab;
